import React, { useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";
import { resizeImage } from "@/lib/image";
import { toast } from "sonner";
import { X, Hand, Loader2, Sparkles, Upload as UploadIcon, LogIn } from "lucide-react";

export default function AskPalmistryModal({ question, onClose }) {
  const { t, lang, user } = useApp();
  const navigate = useNavigate();
  const inputRef = useRef();
  const [image, setImage] = useState(null);
  const [q, setQ] = useState(question || "");
  const [loading, setLoading] = useState(false);
  const [answer, setAnswer] = useState("");

  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try { setImage(await resizeImage(file)); } catch { toast.error("Could not read image"); }
  };

  const submit = async () => {
    if (!image) { toast.error(lang === "hi" ? "कृपया हथेली की फोटो अपलोड करें।" : "Please upload a palm photo."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/qa/ask-palmistry", { image, question: q, lang });
      setAnswer(data.answer);
    } catch (err) {
      if (err?.response?.status === 401) { navigate("/login"); return; }
      toast.error(err?.response?.data?.detail || "Failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] grid place-items-center bg-black/70 backdrop-blur-sm px-4 py-8 overflow-y-auto" data-testid="ask-palmistry-modal">
      <div className="glass-strong rounded-3xl p-7 max-w-lg w-full relative rise-in my-auto">
        <button onClick={onClose} className="absolute top-4 right-4 text-slate-400 hover:text-amber-300" data-testid="ask-palmistry-close"><X size={20} /></button>
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl grid place-items-center bg-gradient-to-br from-amber-500/25 to-purple-600/25 border border-amber-500/30">
            <Hand className="text-amber-300" size={22} />
          </div>
          <h3 className="font-serif text-xl text-amber-100">{t.ask.palmistryTitle}</h3>
        </div>
        <p className="text-sm text-slate-400 mb-5">{t.ask.palmistryDesc}</p>

        <input ref={inputRef} type="file" accept="image/*" className="hidden" onChange={onFile} data-testid="palmistry-file-input" />

        {!user ? (
          <div className="text-center py-6">
            <p className="text-slate-300 mb-4">{t.ask.loginRequired}</p>
            <button onClick={() => navigate("/login")} className="btn-gold rounded-full px-6 py-3 inline-flex items-center gap-2">
              <LogIn size={16} /> {t.nav.login}
            </button>
          </div>
        ) : answer ? (
          <div data-testid="ask-palmistry-answer">
            <div className="text-xs uppercase tracking-widest text-amber-300/70 mb-2 flex items-center gap-1.5"><Sparkles size={13} /> {t.ask.answerTitle}</div>
            <div className="glass rounded-2xl p-5 text-slate-200 leading-relaxed whitespace-pre-line">{answer}</div>
            <p className="mt-3 text-xs text-slate-500">{t.ask.disclaimer}</p>
            <div className="flex gap-3 mt-5">
              <button onClick={() => { setAnswer(""); }} className="btn-ghost-gold rounded-full px-5 py-2.5 text-sm">{t.ask.askAgain}</button>
              <button onClick={onClose} className="btn-gold rounded-full px-5 py-2.5 text-sm">{t.ask.close}</button>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {image ? (
              <div className="relative rounded-2xl overflow-hidden border border-amber-500/25">
                <img src={image} alt="palm" className="w-full max-h-64 object-contain bg-black/40" data-testid="palmistry-preview" />
                <button onClick={() => inputRef.current?.click()} className="absolute bottom-2 right-2 btn-ghost-gold rounded-full px-3 py-1.5 text-xs bg-black/50">{t.ask.changePhoto}</button>
              </div>
            ) : (
              <button onClick={() => inputRef.current?.click()} data-testid="palmistry-choose" className="w-full glass rounded-2xl border-2 border-dashed border-amber-500/30 py-10 flex flex-col items-center gap-3 hover:border-amber-400/60">
                <UploadIcon className="text-amber-300" size={26} />
                <span className="text-sm text-amber-100">{t.ask.choosePhoto}</span>
              </button>
            )}
            <textarea value={q} onChange={(e) => setQ(e.target.value)} placeholder={t.ask.yourQuestion} rows={2} data-testid="palmistry-question" className="w-full rounded-xl bg-purple-950/50 border border-amber-500/25 px-4 py-3 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:border-amber-400/60 resize-none" />
            <button onClick={submit} disabled={loading} data-testid="palmistry-submit" className="btn-gold w-full rounded-full py-3.5 flex items-center justify-center gap-2 gold-glow disabled:opacity-70">
              {loading ? <><Loader2 className="animate-spin" size={18} /> {t.ask.readingPalm}</> : <><Sparkles size={18} /> {t.ask.generate}</>}
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
